import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Inject,
  OnInit,
} from '@angular/core';
import { AsyncPipe } from '@angular/common';
import {
  TuiButtonModule,
  TuiDialogContext,
  TuiTextfieldControllerModule,
} from '@taiga-ui/core';
import { TuiInputModule } from '@taiga-ui/kit';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  BehaviorSubject,
  catchError,
  map,
  of,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { POLYMORPHEUS_CONTEXT } from '@tinkoff/ng-polymorpheus';
import { AlertService } from '@shared/services/alert.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CarrierService } from '@shared/services/carrier.service';
import { InputDateComponent } from '@shared/components/forms/input-date/input-date.component';
import { MaskitoModule } from '@maskito/angular';
import { phoneMask } from '@shared/utils/masks';
import {
  Carrier,
  CarrierForm,
  LogisticianForm,
} from '../../../../types/carrier';

export type CarrierFormInputData = {
  carrier?: Nullable<Carrier>;
};

@Component({
  selector: 'app-carrier-form',
  standalone: true,
  imports: [
    AsyncPipe,
    TuiButtonModule,
    TuiInputModule,
    TuiTextfieldControllerModule,
    ReactiveFormsModule,
    InputDateComponent,
    MaskitoModule,
  ],
  templateUrl: './carrier-form.component.html',
  styleUrl: './carrier-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CarrierFormComponent implements OnInit {
  isEdit$ = new BehaviorSubject(false);
  title$ = this.isEdit$.pipe(
    map(value => (value ? 'Редактировать' : 'Создать').concat(' перевозчика')),
  );

  save$ = new Subject<void>();

  carrierForm = this.fb.group<CarrierForm>({
    id: this.fb.control(null),
    uniqueKey: this.fb.control(null),
    name: this.fb.control(null, Validators.required),
    unp: this.fb.control(null, Validators.required),
    address: this.fb.control(null),
    comment: this.fb.control(null),
    contractDate: this.fb.control(null),
    contractNumber: this.fb.control(null),
    head: this.fb.control(null),
    email: this.fb.control(null, Validators.email),
    logisticiansJson: this.fb.array<FormGroup<LogisticianForm>>([]),
  });

  protected readonly phoneMask = phoneMask;
  constructor(
    @Inject(POLYMORPHEUS_CONTEXT)
    protected readonly context: TuiDialogContext<void, CarrierFormInputData>,
    private readonly fb: FormBuilder,
    private readonly destroyRef: DestroyRef,
    private readonly alert: AlertService,
    private readonly carrierService: CarrierService,
  ) {}

  onSaveClick() {
    this.save$
      .pipe(
        switchMap(() =>
          this.carrierService.save(this.carrierForm.getRawValue()),
        ),
        tap(() => this.closeOnSave()),
        catchError(() => {
          this.alert.showError();
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  closeOnSave() {
    this.context.$implicit.next();
    this.context.$implicit.complete();
  }

  setForm() {
    const carrierJson = this.context.data?.carrier;
    if (carrierJson) {
      const { logisticiansJson, ...carrier } = carrierJson;
      this.carrierForm.patchValue(carrier);
      this.carrierForm.setControl(
        'logisticiansJson',
        this.fb.array(
          logisticiansJson.map(logistician => this.fb.group(logistician)),
        ),
      );
    }
  }

  addLogistician() {
    this.carrierForm.controls.logisticiansJson.push(
      this.fb.group({
        name: this.fb.control(''),
        phone: this.fb.control(''),
      }),
    );
  }

  removeLogistician(index: number) {
    this.carrierForm.controls.logisticiansJson.removeAt(index);
  }

  ngOnInit() {
    this.isEdit$.next(!!this.context.data?.carrier);
    this.setForm();
    this.onSaveClick();
  }
}
